import { Dialog, DialogContent, DialogTitle, IconButton, Grid, TextField, Button, Typography, Box, Divider, Snackbar, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { type CreateBotPayload } from '../../types/api';
import type { ApiContextType } from '../../contexts/ApiContextValue';

interface CadastrarBotModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Callback para quando o bot é cadastrado com sucesso
}

export function CadastrarBotModal({ open, onClose, onSuccess }: CadastrarBotModalProps) {
  const theme = useTheme();
  // const [ofertasExpanded, setOfertasExpanded] = useState(false); // Removido
  const [success, setSuccess] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Usar o hook da API
  const api = useApi();
  // Usando a interface ApiContextType para tipar corretamente
  const { createBot, loading } = api as ApiContextType;

  // Função para criar dados iniciais do bot sem valores padrão
  const createInitialBotData = (): CreateBotPayload => ({
    name: '',
    token: '',
    start: {
      text: '',
      banner: [],
      keyboards: []
    },
    remarkets: {
      start: '',
      product: '',
      payment: '',
    },
  });

  // Estados para os campos do formulário
  const [botData, setBotData] = useState<CreateBotPayload>(createInitialBotData());

  // Efeito para carregar dados salvos do localStorage quando o modal for aberto
  useEffect(() => {
    if (open) {
      const savedData = localStorage.getItem('botFormData');
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          setBotData({
            name: '',
            token: '',
            start: parsed.start || { text: '', banner: [], keyboards: [] },
            remarkets: parsed.remarkets || { start: '', product: '', payment: '' },
          });
        } catch (error) {
          console.error('Erro ao carregar dados do localStorage:', error);
          setBotData(createInitialBotData());
        }
      } else {
        setBotData(createInitialBotData());
      }
    }
  }, [open]);



  // const handleOfertasToggle = () => { // Removido
  //   setOfertasExpanded(!ofertasExpanded);
  // };

  const handleInputChange = (field: string, value: string) => {
    setBotData(prev => ({
      ...prev,
      [field]: value
    }));
  }; const handleStartInputChange = (field: string, value: string, bannerIndex?: number) => {
    setBotData(prev => {
      if (field === 'banner' && typeof bannerIndex === 'number') {
        // Tratamento específico para os banners quando o índice é fornecido
        const newBanner = [...(prev.start?.banner || [])];
        // Garantir que o array tenha espaço suficiente
        while (newBanner.length <= bannerIndex) {
          newBanner.push('');
        }
        newBanner[bannerIndex] = value;

        return {
          ...prev,
          start: {
            ...prev.start!,
            banner: newBanner
          }
        };
      } else {
        // Para outros campos que não são banner ou quando não é fornecido um índice
        return {
          ...prev,
          start: {
            ...prev.start!,
            [field]: value
          }
        };
      }
    });
  };

  const handleRemarketInputChange = (field: string, value: string) => {
    setBotData(prev => ({
      ...prev,
      remarkets: {
        ...prev.remarkets!,
        [field]: value
      }
    }));
  };

  // Função para remover um teclado
  const removeKeyboard = (index: number) => {
    setBotData(prev => ({
      ...prev,
      start: {
        ...prev.start!,
        keyboards: prev.start?.keyboards?.filter((_, i) => i !== index)
      }
    }));
  };

  // Função para adicionar um novo callback
  const addKeyboard = () => {
    setBotData(prev => ({
      ...prev,
      start: {
        ...prev.start!,
        keyboards: [...(prev.start?.keyboards || []), { name: '', text: '' }]
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Derivar callbacks dos keyboards que têm name começando com 'call:'
    const callbackIds = botData.start?.keyboards?.filter(k => k.name.startsWith('call:')).map(k => k.name.replace('call:', '')) || [];

    // Usar os banners atuais do formulário
    const payload: CreateBotPayload = {
      ...botData,
    };

    try {
      const response = await createBot(payload);
      console.log('Bot criado com sucesso:', response);
      setSuccess(true);
      // Salvar dados no localStorage (exceto token e name)
      const formDataToSave = {
        start: botData.start,
        remarkets: botData.remarkets,
        callbacks: callbackIds,
      };
      localStorage.setItem('botFormData', JSON.stringify(formDataToSave));
      // Reset completo do formulário com novos banners aleatórios
      setBotData(createInitialBotData());

      if (onSuccess) {
        onSuccess();
      }
      setTimeout(() => {
        onClose();
      }, 1000);

    } catch (err) {
      console.error('Erro ao cadastrar bot:', err);
      setLocalError(err instanceof Error ? err.message : 'Ocorreu um erro ao cadastrar o bot. Tente novamente.');
    }
  };
  const handleCloseSnackbar = () => {
    setLocalError(null);
    setSuccess(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" PaperProps={{ sx: { borderRadius: 3, bgcolor: theme.palette.mode === 'dark' ? 'rgba(24,31,42,0.98)' : '#fff', boxShadow: 24 } }}>
      <DialogTitle sx={{ p: 3, pb: 0, display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={onClose} sx={{ mr: 2, color: theme.palette.primary.main }}>
          <CloseIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700}>
          Cadastro de Bot
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box component="form" autoComplete="off" onSubmit={handleSubmit}>
          <Grid container spacing={3}> {/* Aumentado o espaçamento geral */}
            {/* Coluna Esquerda: Informações Básicas e Mensagens */}
            <Grid item xs={12} md={6}> {/* Ajustado para md={6} */}
              <TextField
                label="Nome"
                placeholder="Nome do bot"
                fullWidth
                margin="normal"
                value={botData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
              <TextField
                label="Token"
                placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                fullWidth
                margin="normal"
                value={botData.token}
                onChange={(e) => handleInputChange('token', e.target.value)}
                required
              />
              <Divider sx={{ my: 2 }} />

              <Typography fontWeight={600} mb={1} mt={2}>Mensagem de Boas-vindas</Typography>
              <TextField
                label="Texto"
                placeholder="Bem-vindo ao bot!"
                fullWidth
                margin="normal"
                multiline
                rows={4}
                value={botData.start?.text}
                onChange={(e) => handleStartInputChange('text', e.target.value)}
              />              
              <Divider sx={{ my: 2 }} />
              <Typography fontWeight={600} mb={1} mt={2}>Banners</Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setBotData(prev => ({
                    ...prev,
                    start: {
                      ...prev.start!,
                      banner: [...(prev.start?.banner || []), '']
                    }
                  }));
                }}
                sx={{ width: '100%', borderRadius: 2, fontWeight: 600, mt: 1, mb: 2 }}
              >
                Adicionar Banner
              </Button>
              {botData.start?.banner?.map((banner, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                  <TextField
                    label={`Banner ${index + 1}`}
                    value={banner}
                    onChange={(e) => {
                      const newBanners = [...(botData.start?.banner || [])];
                      newBanners[index] = e.target.value;
                      setBotData(prev => ({
                        ...prev,
                        start: {
                          ...prev.start!,
                          banner: newBanners
                        }
                      }));
                    }}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    onClick={() => {
                      setBotData(prev => ({
                        ...prev,
                        start: {
                          ...prev.start!,
                          banner: prev.start?.banner?.filter((_, i) => i !== index)
                        }
                      }));
                    }}
                    size="small"
                    color="error"
                  >
                    Remover
                  </Button>
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />
              <Typography fontWeight={600} mb={1} mt={2}>IDs Remarketing</Typography>
              <TextField
                label="ID de Início"
                placeholder="Mensagem personalizada de início"
                fullWidth
                margin="normal"
                value={botData.remarkets?.start}
                onChange={(e) => handleRemarketInputChange('start', e.target.value)}
              />
              <TextField
                label="ID de Produto"
                placeholder="Mensagem personalizada de produto"
                fullWidth
                margin="normal"
                value={botData.remarkets?.product}
                onChange={(e) => handleRemarketInputChange('product', e.target.value)}
              />
              <TextField
                label="ID de Payment"
                placeholder="Mensagem personalizada de payment"
                fullWidth
                margin="normal"
                value={botData.remarkets?.payment}
                onChange={(e) => handleRemarketInputChange('payment', e.target.value)}
              />
            </Grid>            {/* Coluna Central: Callbacks */}
            <Grid item xs={12} md={6}> {/* Ajustado para md={6} */}
              <Typography fontWeight={600} mb={1} mt={2}>Callbacks</Typography>
              <Button
                variant="contained"
                onClick={addKeyboard}
                sx={{ width: '100%', borderRadius: 2, fontWeight: 600, mt: 1, mb: 2 }}
              >
                Adicionar Callback
              </Button>

              {/* Lista de Callbacks */}
              {botData.start?.keyboards?.map((keyboard, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                  <TextField
                    label={`Nome Callback ${index + 1}`}
                    value={keyboard.name}
                    onChange={(e) => {
                      const newKeyboards = [...(botData.start?.keyboards || [])];
                      if (newKeyboards[index]) {
                        newKeyboards[index].name = e.target.value;
                        setBotData(prev => ({
                          ...prev,
                          start: {
                            ...prev.start!,
                            keyboards: newKeyboards
                          }
                        }));
                      }
                    }}
                    sx={{ flexGrow: 1 }}
                  />
                  <TextField
                    label={`Texto Callback ${index + 1}`}
                    value={keyboard.text}
                    onChange={(e) => {
                      const newKeyboards = [...(botData.start?.keyboards || [])];
                      if (newKeyboards[index]) {
                        newKeyboards[index].text = e.target.value;
                        setBotData(prev => ({
                          ...prev,
                          start: {
                            ...prev.start!,
                            keyboards: newKeyboards
                          }
                        }));
                      }
                    }}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button
                    onClick={() => removeKeyboard(index)}
                    size="small"
                    color="error"
                  >
                    Remover
                  </Button>
                </Box>
              ))}
            </Grid>
          </Grid>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: 4, borderRadius: 2, fontWeight: 700, fontSize: 16, height: 48 }}
            disabled={loading}
          >
            {loading ? 'Cadastrando...' : 'Cadastrar Bot Completo'}
          </Button>
        </Box>
      </DialogContent>
      {/* Feedback de erro ou sucesso */}
      <Snackbar open={!!localError || success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={localError ? "error" : "success"}
          sx={{ width: '100%' }}
        >
          {localError || "Bot cadastrado com sucesso!"}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}